import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { useAuth } from '../auth/AuthProvider.tsx';
import { Button, Card } from '../components/index.ts';
import { AccountSection } from '../features/account/AccountSection.tsx';
import { AppearanceSection } from '../features/account/AppearanceSection.tsx';
import { FeedbackDialog } from '../features/account/FeedbackDialog.tsx';
import { DataSection } from '../features/data/DataSection.tsx';
import { UndoImport } from '../features/data/UndoImport.tsx';
import { RecentlyDeleted } from '../features/entries/RecentlyDeleted.tsx';
import { InstallCard } from '../pwa/InstallCard.tsx';
import { StoragePrompt } from '../sync/StoragePrompt.tsx';
import { SyncProblems } from '../sync/SyncProblems.tsx';

export function SettingsRoute() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  async function handleSignOut(): Promise<void> {
    setSigningOut(true);
    // `signOut` clears local state even if the network call fails, so there is
    // no failure path that leaves someone stuck signed in.
    await signOut();
    void navigate('/login', { replace: true });
  }

  return (
    <div className="flex flex-col gap-4">
      <AccountSection />

      <AppearanceSection />

      <SyncProblems userId={user?.id ?? null} />

      <StoragePrompt />

      {/* Always offered in Settings, whatever the entry count. */}
      <InstallCard force />

      <DataSection userId={user?.id ?? null} displayName={user?.displayName ?? ''} />

      <Card title="Bringing a logbook with you">
        <p className="text-sm text-muted">
          Already keep a logbook in a spreadsheet? Import it — nothing is written until you have
          seen exactly what will come across, and it can be undone afterwards.
        </p>
        <div className="pt-3">
          <Button variant="secondary" onClick={() => void navigate('/import')}>
            Import from a spreadsheet
          </Button>
        </div>
      </Card>

      <UndoImport userId={user?.id ?? null} />

      <RecentlyDeleted userId={user?.id ?? null} />

      <Card title="Session">
        <p className="text-sm text-muted">
          Signing out clears this device’s copy of your logbook. Everything backed up stays on the
          server and comes back when you sign in.
        </p>
        <div className="pt-3">
          <Button variant="secondary" loading={signingOut} onClick={() => void handleSignOut()}>
            Sign out
          </Button>
        </div>
      </Card>

      <Card title="About">
        <ul className="flex flex-col gap-2 text-sm">
          <li>
            <FeedbackDialog />
          </li>
          <li>
            <Link to="/privacy" className="text-accent underline hover:text-accent-hover">
              Privacy notice
            </Link>
          </li>
          <li>
            <Link to="/terms" className="text-accent underline hover:text-accent-hover">
              Terms
            </Link>
          </li>
          {import.meta.env.DEV ? (
            <li>
              <Link to="/styleguide" className="text-accent underline hover:text-accent-hover">
                Component styleguide
              </Link>
            </li>
          ) : null}
          {/*
            Shown only to the operator, and only because the server said so —
            `isOperator` is absent from every other account's /me response, so
            no trainee's browser learns this page exists. Rendering, not
            security: /api/admin/* re-checks the account and the PIN on every
            request regardless of what the client chooses to draw.
          */}
          {user?.isOperator === true ? (
            <li>
              <Link to="/admin/metrics" className="text-accent underline hover:text-accent-hover">
                Operator metrics
              </Link>
            </li>
          ) : null}
        </ul>
      </Card>
    </div>
  );
}
